import React, { ReactNode } from 'react';
import { Flex, Typography } from '@fullstory/components';
import { Arrow } from './Arrow';
import { LibraryObjectResourceType } from '../..';
import { defineMessage, FormattedMessage, MessageDescriptor } from 'react-intl';
import { LocationDescriptorObject } from 'history';

interface PaginationControlsBaseProps {
  page: number;
  disablePrev: boolean;
  disableNext: boolean;
  start: number;
  end: number;
  total: number;
  loading: boolean;
  /**
   * If not provided, a generic message with "items" is used.
   */
  resourceType?: LibraryObjectResourceType;
}

interface PaginationControlsLocationProps extends PaginationControlsBaseProps {
  prevPageLocation: LocationDescriptorObject;
  onPrevPageClick?: never;
  nextPageLocation: LocationDescriptorObject;
  onNextPageClick?: never;
}

interface PaginationControlsOnClickProps extends PaginationControlsBaseProps {
  prevPageLocation?: never;
  onPrevPageClick: () => void;
  nextPageLocation?: never;
  onNextPageClick: () => void;
}

/**
 * Previous and Next page navigation can be done with a location object to form a semantic link,
 * or instead with a callback function.
 */
type PaginationControlsProps = PaginationControlsLocationProps | PaginationControlsOnClickProps;

const DEFAULT_MESSAGE = defineMessage({
  defaultMessage:
    '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b>',
  id: 'GwPT4t',
  description: 'Pager controls specifying how many items are displayed on a page.',
});

const controlsMessages: { [key in LibraryObjectResourceType]: { page: MessageDescriptor } } = {
  Segment: {
    page: defineMessage({
      defaultMessage:
        '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b> {total, plural, one {Segment} other {Segments}}',
      id: 'V1bXqF',
      description: 'Pager controls specifying how many items are displayed on a page.',
    }),
  },
  Metric: {
    page: defineMessage({
      defaultMessage:
        '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b> {total, plural, one {Metric} other {Metrics}}',
      id: 'eYmF9j',
      description: 'Pager controls specifying how many items are displayed on a page.',
    }),
  },
  Journey: {
    page: defineMessage({
      defaultMessage:
        '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b> {total, plural, one {Journey} other {Journeys}}',
      id: 'LYhys1',
      description: 'Pager controls specifying how many items are displayed on a page.',
    }),
  },
  Heatmap: {
    page: defineMessage({
      defaultMessage:
        '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b> {total, plural, one {Heatmap} other {Heatmaps}}',
      id: 'iDDk2P',
      description: 'Pager controls specifying how many items are displayed on a page.',
    }),
  },
  Funnel: {
    page: defineMessage({
      defaultMessage:
        '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b> {total, plural, one {Funnel} other {Funnels}}',
      id: 'GdUH67',
      description: 'Pager controls specifying how many items are displayed on a page.',
    }),
  },
  Dashboard: {
    page: defineMessage({
      defaultMessage:
        '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b> {total, plural, one {Dashboard} other {Dashboards}}',
      id: 'nn8b4O',
      description: 'Pager controls specifying how many items are displayed on a page.',
    }),
  },
  Retention: {
    page: defineMessage({
      defaultMessage:
        '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b> {total, plural, one {Retention} other {Retentions}}',
      id: 'fEtEKy',
      description: 'Pager controls specifying how many items are displayed on a page.',
    }),
  },
  Space: {
    page: defineMessage({
      defaultMessage:
        '<b>{end, select, 0 {0} other {{start, number}-{end, number}}}</b> of <b>{total, number}</b> {total, plural, one {Space} other {Spaces}}',
      id: 'JTBvnO',
      description: 'Pager controls specifying how many items are displayed on a page.',
    }),
  },
};

const boldTypography = (chunks: ReactNode) => (
  <Typography
    data-component="boldTypography"
    fontWeight="semibold"
    variant="body3"
    color="neutrals-n12"
  >
    {chunks}
  </Typography>
);

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  loading,
  prevPageLocation,
  onPrevPageClick,
  nextPageLocation,
  onNextPageClick,
  page,
  total,
  start,
  end,
  disablePrev,
  disableNext,
  resourceType,
}) => (
  <Flex
    data-component="PaginationControls"
    data-testid="footer"
    alignItems="center"
    justifyContent="space-evenly"
  >
    <Arrow
      page={page}
      loading={loading}
      disabled={disablePrev}
      icon="previous"
      to={prevPageLocation}
      onClick={onPrevPageClick}
    />
    <div className="fs-mask">
      <Typography variant="body3" color="neutrals-n12">
        <FormattedMessage
          {...(resourceType ? controlsMessages[resourceType].page : DEFAULT_MESSAGE)}
          values={{
            total,
            start,
            end,
            b: boldTypography,
          }}
        />
      </Typography>
    </div>
    <Arrow
      page={page}
      loading={loading}
      disabled={disableNext}
      icon="next"
      to={nextPageLocation}
      onClick={onNextPageClick}
    />
  </Flex>
);
